import React, { useState, useRef } from 'react';
import { scanDocument } from '../utils/ocrUtils';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';

/**
 * OCRScanner — scan ID cards, lab reports, invoices and extract data.
 *
 * Props:
 *   mode       — 'registration' | 'insurance' | 'lab' | 'expense' | 'pharmacy'
 *   onDataExtracted(parsed, docType) — callback with extracted data
 *   onClose
 */
const OCRScanner = ({ mode, onDataExtracted, onClose }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [docType, setDocType] = useState('auto');
  const [inputMode, setInputMode] = useState('upload'); // upload | camera | scanner
  const [scannerIP, setScannerIP] = useState(() => localStorage.getItem('scannerIP') || '');
  const [scannerLoading, setScannerLoading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const docTypeOptions = {
    registration: [
      { key: 'auto', label: 'Auto Detect' },
      { key: 'aadhaar', label: 'Aadhaar Card' },
      { key: 'pan', label: 'PAN Card' },
      { key: 'dl', label: 'Driving License' },
    ],
    insurance: [
      { key: 'insurance', label: 'Insurance Card' },
    ],
    lab: [
      { key: 'lab', label: 'Lab Report' },
    ],
    expense: [
      { key: 'invoice', label: 'Bill / Invoice' },
    ],
    pharmacy: [
      { key: 'invoice', label: 'Supplier Invoice' },
    ],
  };

  const options = docTypeOptions[mode] || docTypeOptions.registration;

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  // Camera capture — opens device camera in document scan mode
  const handleCameraCapture = () => {
    if (cameraRef.current) cameraRef.current.click();
  };

  const handleCameraFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  // Network Scanner — fetch from WiFi scanner's web interface
  const handleNetworkScan = async () => {
    if (!scannerIP.trim()) return alert('Enter scanner IP address');
    setScannerLoading(true);
    localStorage.setItem('scannerIP', scannerIP.trim());

    // Common scanner web API endpoints (HP, Epson, Canon, Brother)
    const endpoints = [
      `http://${scannerIP}/eSCL/ScanJobs`,          // eSCL (AirScan standard — HP, Epson, Canon)
      `http://${scannerIP}/cgi-bin/scanner/scan`,    // Brother
      `http://${scannerIP}:8080/scan`,               // Generic
      `http://${scannerIP}/WebServices/Scanner`,      // WSD
    ];

    try {
      // Method 1: Try eSCL (industry standard for network scanning)
      const scanPayload = `<?xml version="1.0" encoding="UTF-8"?>
        <scan:ScanSettings xmlns:scan="http://schemas.hp.com/imaging/escl/2011/05/03"
                           xmlns:pwg="http://www.pwg.org/schemas/2010/12/sm">
          <pwg:Version>2.0</pwg:Version>
          <scan:Intent>Document</scan:Intent>
          <pwg:ScanRegions><pwg:ScanRegion>
            <pwg:Height>3507</pwg:Height><pwg:Width>2480</pwg:Width>
            <pwg:XOffset>0</pwg:XOffset><pwg:YOffset>0</pwg:YOffset>
          </pwg:ScanRegion></pwg:ScanRegions>
          <pwg:InputSource>Platen</pwg:InputSource>
          <scan:ColorMode>RGB24</scan:ColorMode>
          <scan:XResolution>300</scan:XResolution>
          <scan:YResolution>300</scan:YResolution>
          <pwg:DocumentFormat>image/jpeg</pwg:DocumentFormat>
        </scan:ScanSettings>`;

      // Try eSCL first
      try {
        const res = await fetch(`http://${scannerIP}/eSCL/ScanJobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'text/xml' },
          body: scanPayload,
        });
        if (res.ok || res.status === 201) {
          const jobUrl = res.headers.get('Location') || `http://${scannerIP}/eSCL/ScanJobs/1/NextDocument`;
          // Wait for scan to complete
          await new Promise(r => setTimeout(r, 3000));
          const imgRes = await fetch(jobUrl);
          if (imgRes.ok) {
            const blob = await imgRes.blob();
            const dataUrl = await new Promise(resolve => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            setPreview(dataUrl);
            setFile(null);
            setScannerLoading(false);
            return;
          }
        }
      } catch (e) { /* eSCL not supported, try next */ }

      // Method 2: Direct image fetch (some scanners serve last scan)
      try {
        const res = await fetch(`http://${scannerIP}/scan.jpg`, { mode: 'no-cors' });
        if (res.ok) {
          const blob = await res.blob();
          const dataUrl = URL.createObjectURL(blob);
          setPreview(dataUrl);
          setScannerLoading(false);
          return;
        }
      } catch (e) { /* not available */ }

      // If all methods fail — show manual instruction
      alert(
        `Could not connect to scanner at ${scannerIP}.\n\n` +
        `Try these steps:\n` +
        `1. Open your scanner's web panel: http://${scannerIP}\n` +
        `2. Scan the document from there\n` +
        `3. Save as JPG/PNG\n` +
        `4. Use "Upload File" to load the saved image\n\n` +
        `Or use "Camera" to take a photo instead.`
      );
    } catch (e) {
      alert('Scanner connection failed: ' + e.message);
    }
    setScannerLoading(false);
  };

  const handleScan = async () => {
    if (!file && !preview) return alert('Select or capture an image first');
    setScanning(true);
    setProgress(10);

    try {
      const progressInterval = setInterval(() => {
        setProgress(p => Math.min(p + 8, 90));
      }, 500);

      const res = await scanDocument(file || preview, docType === 'auto' ? 'auto' : docType);
      clearInterval(progressInterval);
      setProgress(100);
      setResult(res);
    } catch (e) {
      alert('Scan failed: ' + e.message);
    }
    setScanning(false);
  };

  const handleApply = () => {
    if (result?.parsed && onDataExtracted) {
      onDataExtracted(result.parsed, result.docType);
    }
    onClose();
  };

  const docTypeLabels = {
    aadhaar: 'Aadhaar Card',
    pan: 'PAN Card',
    dl: 'Driving License',
    insurance: 'Insurance Card',
    lab: 'Lab Report',
    invoice: 'Invoice / Bill',
    unknown: 'Unknown Document',
  };

  const modeLabels = {
    registration: 'Scan ID Card',
    insurance: 'Scan Insurance Card',
    lab: 'Scan Lab Report',
    expense: 'Scan Bill / Receipt',
    pharmacy: 'Scan Supplier Invoice',
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '16px',
          width: '800px', maxWidth: '95vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>

        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #eee',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, color: theme.navy }}>📷 {modeLabels[mode] || 'OCR Scanner'}</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: theme.textMuted }}>
              Take a photo or upload an image — AI will extract data automatically
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#aaa' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* Document type selector */}
          {options.length > 1 && (
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Document Type</label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {options.map(o => (
                  <button key={o.key} onClick={() => setDocType(o.key)}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                      fontWeight: '600', fontSize: '0.82rem',
                      backgroundColor: docType === o.key ? theme.primary : '#e9ecef',
                      color: docType === o.key ? 'white' : '#555',
                    }}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input mode: Upload / Camera / Network Scanner */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Input Source</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {[
                { key: 'upload', icon: '📁', label: 'Upload File' },
                { key: 'camera', icon: '📷', label: 'Camera' },
                { key: 'scanner', icon: '🖨️', label: 'Network Scanner' },
              ].map(m => (
                <button key={m.key} onClick={() => setInputMode(m.key)}
                  style={{
                    padding: '10px 20px', borderRadius: '10px', border: `2px solid ${inputMode === m.key ? theme.primary : theme.border}`,
                    cursor: 'pointer', fontWeight: '600', fontSize: '0.86rem',
                    backgroundColor: inputMode === m.key ? theme.primaryLight : 'white',
                    color: inputMode === m.key ? theme.primaryDark : theme.navy,
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                  <span style={{ fontSize: '1.1rem' }}>{m.icon}</span> {m.label}
                </button>
              ))}
            </div>

            {/* Upload File */}
            {inputMode === 'upload' && (
              <input type="file" accept="image/*,.pdf" onChange={handleFile} ref={fileRef}
                style={{ ...inputStyle, padding: '7px' }} />
            )}

            {/* Camera — uses device camera with document mode */}
            {inputMode === 'camera' && (
              <div>
                <input type="file" accept="image/*" capture="environment" onChange={handleCameraFile}
                  ref={cameraRef} style={{ display: 'none' }} />
                <button onClick={handleCameraCapture}
                  style={{
                    padding: '14px 28px', border: `2px dashed ${theme.primary}`, borderRadius: '12px',
                    backgroundColor: theme.primaryLight, color: theme.primaryDark,
                    cursor: 'pointer', fontWeight: '700', fontSize: '0.95rem',
                    width: '100%', textAlign: 'center',
                  }}>
                  📷 Open Camera & Capture Document
                </button>
                <p style={{ fontSize: '0.78rem', color: theme.textMuted, marginTop: '6px' }}>
                  Hold the document flat, ensure good lighting. The camera will auto-focus.
                </p>
              </div>
            )}

            {/* Network Scanner */}
            {inputMode === 'scanner' && (
              <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Scanner IP Address</label>
                    <input value={scannerIP} onChange={e => setScannerIP(e.target.value)}
                      placeholder="e.g. 192.168.0.100"
                      style={inputStyle}
                      onKeyDown={e => { if (e.key === 'Enter') handleNetworkScan(); }} />
                  </div>
                  <button onClick={handleNetworkScan} disabled={scannerLoading}
                    style={{
                      padding: '10px 22px', backgroundColor: theme.navy, color: 'white',
                      border: 'none', borderRadius: '8px', cursor: scannerLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '700', whiteSpace: 'nowrap',
                    }}>
                    {scannerLoading ? '⏳ Scanning...' : '🖨️ Scan Now'}
                  </button>
                </div>
                <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: 0 }}>
                  Place the document on your scanner's glass, then click "Scan Now".
                  <br/>Works with HP, Epson, Canon, Brother WiFi scanners on the same network.
                  <br/>Scanner must be powered on and connected to your WiFi.
                </p>
                {scannerIP && (
                  <p style={{ fontSize: '0.75rem', color: theme.textSubtle, marginTop: '6px' }}>
                    💡 Can't connect? Open <a href={`http://${scannerIP}`} target="_blank" rel="noreferrer" style={{ color: theme.primary }}>http://{scannerIP}</a> in browser to check if scanner is reachable.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Preview + Scan */}
          {preview && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <img src={preview} alt="Document"
                    style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '10px', border: '1px solid #eee' }} />
                </div>
                {!result && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button onClick={handleScan} disabled={scanning}
                      style={{
                        padding: '14px 28px',
                        background: scanning
                          ? '#6c757d'
                          : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                        color: 'white', border: 'none', borderRadius: '10px',
                        cursor: scanning ? 'not-allowed' : 'pointer',
                        fontWeight: '700', fontSize: '0.95rem',
                      }}>
                      {scanning ? `Scanning... ${progress}%` : '🤖 Scan with AI'}
                    </button>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {scanning && (
                <div style={{ height: '6px', backgroundColor: '#f0f0f0', borderRadius: '3px', marginTop: '10px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${progress}%`, height: '100%',
                    background: `linear-gradient(90deg, ${theme.primary}, ${theme.primaryDark})`,
                    borderRadius: '3px', transition: 'width 0.3s',
                  }} />
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {result && (
            <div>
              {/* Document type detected */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
                backgroundColor: theme.primaryLight, borderRadius: '8px', marginBottom: '14px',
              }}>
                <span style={{ fontSize: '1.2rem' }}>✓</span>
                <span style={{ fontWeight: '600', color: theme.primaryDark }}>
                  Detected: {docTypeLabels[result.docType] || result.docType}
                </span>
              </div>

              {/* Extracted data */}
              <div style={{ ...cardStyle, marginBottom: '14px' }}>
                <h4 style={{ margin: '0 0 12px', color: theme.navy }}>Extracted Data</h4>
                {Object.keys(result.parsed).length === 0 ? (
                  <p style={{ color: theme.textMuted }}>Could not extract structured data. Check the raw text below.</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {Object.entries(result.parsed).map(([key, val]) => {
                      if (key === 'tests' && Array.isArray(val)) return null; // handled separately
                      return (
                        <div key={key} style={{ padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                          <div style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                          <div style={{ fontSize: '0.92rem', color: theme.navy, fontWeight: '600', marginTop: '2px' }}>{String(val)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Lab test results */}
                {result.parsed.tests?.length > 0 && (
                  <div style={{ marginTop: '14px' }}>
                    <h4 style={{ margin: '0 0 8px', color: theme.navy }}>Lab Test Values</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: '0.74rem', color: theme.textMuted }}>TEST</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: '0.74rem', color: theme.textMuted }}>VALUE</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: '0.74rem', color: theme.textMuted }}>UNIT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.parsed.tests.map((t, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '6px 10px', fontWeight: '600' }}>{t.name}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '700', color: theme.navy }}>{t.value}</td>
                            <td style={{ padding: '6px 10px', color: theme.textMuted }}>{t.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Raw text (collapsible) */}
              <details style={{ marginBottom: '14px' }}>
                <summary style={{ cursor: 'pointer', color: theme.textMuted, fontSize: '0.85rem', fontWeight: '600' }}>
                  Show Raw OCR Text
                </summary>
                <pre style={{
                  marginTop: '8px', padding: '12px', backgroundColor: '#f8f9fa',
                  borderRadius: '8px', fontSize: '0.78rem', whiteSpace: 'pre-wrap',
                  maxHeight: '200px', overflowY: 'auto', color: theme.navy,
                }}>
                  {result.rawText}
                </pre>
              </details>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={handleApply}
                  style={{
                    padding: '12px 32px',
                    background: `linear-gradient(135deg, ${theme.success} 0%, #059669 100%)`,
                    color: 'white', border: 'none', borderRadius: '10px',
                    cursor: 'pointer', fontWeight: '700', fontSize: '1rem',
                  }}>
                  ✓ Apply Extracted Data
                </button>
                <button onClick={() => { setResult(null); setFile(null); setPreview(null); setProgress(0); }}
                  style={{ padding: '10px 24px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                  Scan Another
                </button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!preview && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.3 }}>📷</div>
              <h3 style={{ color: theme.navy, margin: '0 0 8px' }}>Upload or Capture Document</h3>
              <p style={{ color: theme.textMuted, margin: 0 }}>
                {mode === 'registration' && 'Scan Aadhaar, PAN, or Driving License to auto-fill patient details'}
                {mode === 'insurance' && 'Scan insurance card to extract policy details'}
                {mode === 'lab' && 'Scan lab report to extract test values'}
                {mode === 'expense' && 'Scan vendor bill to auto-fill expense entry'}
                {mode === 'pharmacy' && 'Scan supplier invoice to auto-fill purchase entry'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OCRScanner;
